import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { useLocationListing } from '../hooks/useLocationListing';
import { LocationControls, DistanceBadge } from '../components/LocationControls';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import GuestBanner from '../components/GuestBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Dog,
  Heart,
  GraduationCap,
  Building,
  Shield,
  Stethoscope,
  Scissors,
  Trophy,
  Accessibility,
  HeartHandshake,
  Book,
  Users,
  Plus,
  Search,
  Globe,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Loader2,
  CheckCircle,
  ExternalLink,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const orgIcons = {
  breed_club: Dog,
  rescue: Heart,
  training_club: GraduationCap,
  kennel_club: Building,
  pet_welfare: Shield,
  veterinary_association: Stethoscope,
  grooming_association: Scissors,
  agility_club: Trophy,
  service_dog_org: Accessibility,
  therapy_dog_org: HeartHandshake,
  breed_registry: Book,
  other: Users
};

const orgColors = {
  breed_club: 'bg-amber-100 text-amber-600',
  rescue: 'bg-red-100 text-red-600',
  training_club: 'bg-blue-100 text-blue-600',
  kennel_club: 'bg-slate-100 text-slate-600',
  pet_welfare: 'bg-green-100 text-green-600',
  veterinary_association: 'bg-pink-100 text-pink-600',
  grooming_association: 'bg-purple-100 text-purple-600',
  agility_club: 'bg-orange-100 text-orange-600',
  service_dog_org: 'bg-indigo-100 text-indigo-600',
  therapy_dog_org: 'bg-rose-100 text-rose-600',
  breed_registry: 'bg-cyan-100 text-cyan-600',
  other: 'bg-gray-100 text-gray-600'
};

const Organizations = () => {
  const { user } = useAuth();
  
  // Use the location listing hook for organizations
  const {
    items: organizations,
    loading,
    userLocation,
    locationLoading,
    requestLocation,
    clearLocation,
    sortBy,
    setSortBy,
    maxDistance,
    setMaxDistance,
    filters,
    updateFilter,
    refetch
  } = useLocationListing({
    endpoint: '/api/organizations',
    buildParams: useCallback((filters) => {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.type && filters.type !== 'all') params.organization_type = filters.type;
      if (filters.partner === 'partners') params.is_partner = true;
      if (filters.partner === 'verified') params.is_verified = true;
      return params;
    }, []),
    initialFilters: {
      search: '',
      type: 'all',
      partner: 'all'
    }
  });

  const [orgTypes, setOrgTypes] = useState([]);
  const [myMemberships, setMyMemberships] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    organization_type: 'breed_club',
    description: '',
    mission_statement: '',
    founded_year: '',
    website: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    pet_types_served: ['dog'],
    is_nonprofit: true
  });

  useEffect(() => {
    fetchOrgTypes();
    if (user) {
      fetchMyMemberships();
    }
  }, [user]);

  const fetchOrgTypes = async () => {
    try {
      const response = await axios.get(`${API}/organizations/types`);
      setOrgTypes(response.data);
    } catch (error) {
      console.error('Error fetching org types:', error);
    }
  };

  const fetchMyMemberships = async () => {
    try {
      const response = await axios.get(`${API}/organizations/me/memberships`);
      setMyMemberships(response.data);
    } catch (error) {
      setMyMemberships([]);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      organization_type: 'breed_club',
      description: '',
      mission_statement: '',
      founded_year: '',
      website: '',
      email: '',
      phone: '',
      city: '',
      state: '',
      country: '',
      pet_types_served: ['dog'],
      is_nonprofit: true
    });
  };

  const handleCreate = async () => {
    if (!form.name || !form.description) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        founded_year: form.founded_year ? parseInt(form.founded_year) : null
      };
      await axios.post(`${API}/organizations`, payload);
      toast.success('Organization created!');
      setAddDialogOpen(false);
      resetForm();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  const handleJoin = async (orgId) => {
    try {
      await axios.post(`${API}/organizations/${orgId}/join`, {
        organization_id: orgId
      });
      toast.success('Joined organization!');
      fetchMyMemberships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeave = async (orgId) => {
    if (!window.confirm('Are you sure you want to leave this organization?')) return;
    
    try {
      await axios.delete(`${API}/organizations/${orgId}/leave`);
      toast.success('Left organization');
      fetchMyMemberships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const isMember = (orgId) => {
    return myMemberships.some(m => m.organization_id === orgId);
  };

  const OrgIcon = ({ type }) => {
    const Icon = orgIcons[type] || Users;
    return <Icon className="w-5 h-5" />;
  };

  if (loading && organizations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Organizations & Clubs"
        description="Connect with breed clubs, rescues, and pet organizations. Join your local dog community through All American Dog Club."
        url="/organizations"
      />
      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to join organizations, add your club, and connect with other members" />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              Organizations & Clubs
            </h1>
            <p className="text-muted-foreground">
              Connect with breed clubs, rescues, and pet organizations
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {user && (
              <Dialog open={addDialogOpen} onOpenChange={(open) => {
                setAddDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-org-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Organization
                  </Button>
                </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Organization</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Input
                        placeholder="Organization Name *"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    <Select
                      value={form.organization_type}
                      onValueChange={(val) => setForm({ ...form, organization_type: val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {orgTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Founded Year"
                      type="number"
                      value={form.founded_year}
                      onChange={(e) => setForm({ ...form, founded_year: e.target.value })}
                    />
                  </div>
                  
                  <Textarea
                    placeholder="Description *"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                  />
                  
                  <Textarea
                    placeholder="Mission Statement"
                    value={form.mission_statement}
                    onChange={(e) => setForm({ ...form, mission_statement: e.target.value })}
                    rows={2}
                  />

                  <Input
                    placeholder="Website"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <Input
                      placeholder="Phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      placeholder="City"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                    />
                    <Input
                      placeholder="State"
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                    />
                    <Input
                      placeholder="Country"
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                    />
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_nonprofit}
                      onChange={(e) => setForm({ ...form, is_nonprofit: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">This is a nonprofit organization</span>
                  </label>

                  <Button onClick={handleCreate} className="w-full btn-primary" disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Create Organization
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Location Controls */}
        <div className="flex flex-wrap gap-4 mb-6 items-center">
          <LocationControls
            userLocation={userLocation}
            locationLoading={locationLoading}
            onRequestLocation={requestLocation}
            onClearLocation={clearLocation}
            maxDistance={maxDistance}
            onMaxDistanceChange={setMaxDistance}
            sortBy={sortBy}
            onSortByChange={setSortBy}
          />
        </div>

        {/* My Memberships */}
        {user && myMemberships.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-lg mb-3">My Organizations</h2>
            <div className="flex flex-wrap gap-2">
              {myMemberships.map((membership) => (
                <Badge 
                  key={membership.id} 
                  variant="secondary"
                  className="px-3 py-1.5 cursor-pointer hover:bg-secondary/80"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {membership.organization_name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search organizations..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10"
              data-testid="org-search"
            />
          </div>
          <Select value={filters.type} onValueChange={(val) => updateFilter('type', val)}>
            <SelectTrigger className="w-48" data-testid="org-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {orgTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.partner} onValueChange={(val) => updateFilter('partner', val)}>
            <SelectTrigger className="w-40" data-testid="org-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="partners">Partners Only</SelectItem>
              <SelectItem value="verified">Verified Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Organizations Grid */}
        {organizations.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">No organizations found</h3>
              <p className="text-muted-foreground">
                {loading ? "Loading..." : "Be the first to add an organization!"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {organizations.map((org) => {
              const colorClass = orgColors[org.organization_type] || 'bg-gray-100 text-gray-600';
              const typeInfo = orgTypes.find(t => t.id === org.organization_type);
              const memberStatus = isMember(org.id);

              return (
                <Card key={org.id} className="card-interactive" data-testid={`org-${org.id}`}>
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {org.logo_url ? (
                          <img 
                            src={org.logo_url} 
                            alt={org.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClass}`}>
                            <OrgIcon type={org.organization_type} />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-medium">{org.name}</h3>
                            {org.is_verified && (
                              <CheckCircle className="w-4 h-4 text-blue-500" />
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {typeInfo?.name || org.organization_type}
                          </Badge>
                        </div>
                      </div>
                      {org.is_partner && (
                        <Badge className="bg-primary/10 text-primary border-primary/20">
                          Partner
                        </Badge>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {org.description}
                    </p>

                    {/* Info */}
                    <div className="space-y-1 mb-4 text-sm">
                      {(org.city || org.state) && (
                        <div className="flex items-center justify-between text-muted-foreground">
                          <p className="flex items-center gap-2">
                            <MapPin className="w-3 h-3" />
                            {org.city}{org.state && `, ${org.state}`}
                          </p>
                          <DistanceBadge distance={org.distance_miles} />
                        </div>
                      )}
                      {org.founded_year && (
                        <p className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          Founded {org.founded_year}
                        </p>
                      )}
                      <p className="flex items-center gap-2 text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {org.member_count} member{org.member_count !== 1 ? 's' : ''}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {org.is_nonprofit && (
                        <Badge variant="outline" className="text-xs">
                          Nonprofit
                        </Badge>
                      )}
                      {org.pet_types_served?.map((pt) => (
                        <Badge key={pt} variant="outline" className="text-xs capitalize">
                          {pt.replace('_', ' ')}s
                        </Badge>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {org.website && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(org.website, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      {user && (
                        memberStatus ? (
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleLeave(org.id)}
                          >
                            <UserMinus className="w-4 h-4 mr-2" />
                            Leave
                          </Button>
                        ) : (
                          <Button 
                            className="flex-1 btn-primary"
                            onClick={() => handleJoin(org.id)}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Join
                          </Button>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Organizations;
